import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  RadioButton,
  Input,
  FlexBox,
  FlexBoxDirection,
  Button,
  MessageStrip,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function OrdersConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const purchaseOrderSettings = systemSettings?.PURCHASE_ORDERS;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('OrdersConfig - Current Values:', { formData, purchaseOrderSettings });
  }, [formData, purchaseOrderSettings]);

  if (!formData || !purchaseOrderSettings) {
    return <div>Error: Faltan datos necesarios para la configuración de órdenes</div>;
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleStatusLabelChange = (key, value) => {
    setFormData({
      ...formData,
      orderStatusLabels: { ...formData.orderStatusLabels, [key.toLowerCase()]: value },
    });
  };

  const prepareDataForSave = () => ({
    ...purchaseOrderSettings,
    schedulingType: {
      ...purchaseOrderSettings.schedulingType,
      default: formData.schedulingType,
    },
    statusOptions: purchaseOrderSettings.statusOptions.map(status => ({
      ...status,
      customLabel: formData.orderStatusLabels[status.value.toLowerCase()] || status.customLabel,
    })),
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () =>
    formData.schedulingType !== purchaseOrderSettings.schedulingType.default ||
    purchaseOrderSettings.statusOptions.some(
      status => formData.orderStatusLabels[status.value.toLowerCase()] !== status.customLabel
    );

  const selectedSchedulingLabel =
    purchaseOrderSettings.schedulingType.options.find(o => o.value === formData.schedulingType)?.label ||
    autoT('No seleccionado');

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración de Órdenes de Compra')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Configure los estados y el tipo de agendamiento de las órdenes')}
          </Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Estados de órdenes */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Etiquetas de Estados')}</Title>
        </div>
        <Text style={{ ...CONFIG_STYLES.labelHint, display: 'block', marginBottom: '1rem' }}>
          {autoT('Personalice los nombres de los estados que se mostrarán en el sistema')}
        </Text>
        <div style={CONFIG_STYLES.formGrid}>
          {purchaseOrderSettings.statusOptions.map(status => (
            <div key={status.value} style={CONFIG_STYLES.fieldGroup}>
              <Label>{status.originalLabel}</Label>
              <Input
                value={formData.orderStatusLabels[status.value.toLowerCase()] || status.customLabel}
                style={{ width: '100%' }}
                onChange={(e) => handleStatusLabelChange(status.value, e.target.value)}
                placeholder={status.customLabel}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tipo de agendamiento */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Configuración de Agendamiento')}</Title>
        </div>
        <Text style={{ ...CONFIG_STYLES.labelHint, display: 'block', marginBottom: '1rem' }}>
          {autoT('Seleccione el tipo de agendamiento para las órdenes')}
        </Text>

        <div style={CONFIG_STYLES.fieldGroup}>
          <Label>{autoT('Tipo de Agendamiento')}</Label>
          <FlexBox direction={FlexBoxDirection.Column} style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
            {purchaseOrderSettings.schedulingType.options.map(option => (
              <RadioButton
                key={option.value}
                text={option.label}
                value={option.value}
                name="schedulingType"
                checked={formData.schedulingType === option.value}
                onChange={() => handleInputChange('schedulingType', option.value)}
              />
            ))}
          </FlexBox>
        </div>
      </div>

      {/* Resumen */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="detail-view" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Resumen de Configuraciones')}</Title>
        </div>
        <div style={{
          background: 'var(--sapBackgroundColor)',
          padding: '1rem',
          borderRadius: '0.375rem',
        }}>
          <Text>
            <strong>{autoT('Tipo de Agendamiento:')}</strong> {selectedSchedulingLabel}
          </Text>
          <br />
          <Text style={{ ...CONFIG_STYLES.labelHint, display: 'block', marginTop: '0.75rem' }}>
            <strong>{autoT('Estados Personalizados:')}</strong>
          </Text>
          {purchaseOrderSettings.statusOptions.map(status => (
            <Text key={status.value} style={{ ...CONFIG_STYLES.labelHint, display: 'block' }}>
              {status.originalLabel}: {formData.orderStatusLabels[status.value.toLowerCase()] || status.customLabel}
            </Text>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OrdersConfig;
